import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import StarRatings from "react-star-ratings";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { formatAppDateTime } from "util/formatAppDate";
import { EditOutlined } from "@ant-design/icons";
import { notification, Select, Button } from "antd";
import { apiRequest } from "util/services";
import Dropdown from "helperComponents/CustomDropdown";
import {
  applyDrawerClassifierFeedback,
  applyDrawerTopicFeedback,
} from "pages/Welcome/applyDrawerTopicFeedback";
import RangeBar from "components/Dashboard/RangeBar";
import {
  normalizeWelcomeDrawerReviewDoc,
  welcomeDrawerReviewPivotEntries,
} from "pages/Welcome/welcomeDrawerReviewDoc";
import WelcomeDrawerReviewTextHighlight from "pages/Welcome/WelcomeDrawerReviewTextHighlight";
import {
  canOpenDrawerFeedback,
  isEmbedFeedbackReview,
  resolveDrawerFeedbackUrlAsync,
} from "pages/Welcome/welcomeDrawerReviewFeedbackLink";
import {
  buildDrawerOverallIntentOptions,
  buildDrawerOverallSentimentOptions,
  buildDrawerTopicSentimentOptions,
  buildDrawerSequenceMeta,
  buildDrawerClassifierMeta,
  findDrawerSelectOption,
  resolveDrawerSentimentMeta,
  resolveDrawerTopicTags,
  resolveDrawerTopicTagsFromHighlight,
  resolveTopicTagColors,
  resolveTopicTagIntentIcon,
  resolveTopicTagSentimentIcon,
} from "pages/Welcome/welcomeDrawerReviewMeta";
import { translateDecisionLabel } from "util/analysisLabelI18n";
import ReviewActionTag from "components/Dashboard/ReviewActionTag";
import styles from "pages/Welcome/WelcomeDrawerSimpleReviewCard.module.scss";
import {
  drawerTopicKey,
  hasPendingDrawerTopicEdits,
  isDrawerTopicIsolated,
  nextClickedTopicKey,
  nextDrawerTopicOverride,
  shouldRenderIsolatedTopicDropdowns,
  visibleDrawerTopicTags,
} from "pages/Welcome/welcomeDrawerTopicIsolation";

function formatReviewDate(ts, language) {
  return formatAppDateTime(ts, language);
}

const CASE_DECISIONS = new Set(["Open a case", "Do not open a case"]);
const ACTION_DECISIONS = new Set(["Take action", "Do not take action"]);
const PUBLISH_DECISIONS = new Set(["Publish", "Do not Publish"]);

function shouldShowDrawerClassifierField(clickedDecision, field) {
  if (!clickedDecision) return true;
  if (field === "mPublish") {
    return ![...CASE_DECISIONS, ...ACTION_DECISIONS].includes(clickedDecision);
  }
  if (field === "mOpenCase") {
    return ![...PUBLISH_DECISIONS, ...ACTION_DECISIONS].includes(clickedDecision);
  }
  return ![...PUBLISH_DECISIONS, ...CASE_DECISIONS].includes(clickedDecision);
}

function parseDrawerClassifierSelect(value) {
  if (value === "1") return true;
  if (value === "2") return false;
  return null;
}

function drawerClassifierSelectValue(value) {
  if (value === true) return "1";
  if (value === false) return "2";
  return undefined;
}

function buildDrawerClassifierOptions(t) {
  return {
    publish: [
      { value: "1", label: "Could be published" },
      { value: "2", label: "Do not publish" },
    ],
    openCase: [
      { value: "1", label: "Open a case" },
      { value: "2", label: "Do not open any case" },
    ],
    action: [
      { value: "1", label: "Take an action" },
      { value: "2", label: "Do not take any action" },
    ],
    placeholders: {
      publish: t("button.48", "Select Publish Status"),
      openCase: t("button.49", "Open a new case"),
      action: t("button.50", "Take an immediate action"),
    },
  };
}

function DrawerPill({ icon, label, background, color }) {
  if (!label) return null;
  return (
    <span
      className={styles.pill}
      style={
        background || color
          ? { backgroundColor: background, color }
          : undefined
      }
    >
      {icon ? <img src={icon} alt="" /> : null}
      <span>{label}</span>
    </span>
  );
}

/**
 * Read-only review row for Welcome / KPI side drawers — console ReviewsList parity.
 */
export default function WelcomeDrawerSimpleReviewCard({
  review,
  highlightEntry = null,
  theme = "light",
  onHighlightsRefresh,
  feedbackModeEnabled = false,
}) {
  const { t, i18n } = useTranslation();
  const isFeedbackEnabled = useSelector(
    (state) => state.userReducer?.isFeedbackEnabled,
  );
  const doc = useMemo(
    () => normalizeWelcomeDrawerReviewDoc(review) || review,
    [review],
  );
  const text = String(doc?.text ?? "").trim();
  const title = String(doc?.title ?? "").trim();
  const dateLabel = formatReviewDate(doc?.timestamp, i18n.language);
  const rating = Number(doc?.num_rating);

  const sequences = highlightEntry?.sequences ?? null;
  const classifierSequences = highlightEntry?.classifier_sequences ?? null;
  const associatedTopics = highlightEntry?.associated_topics ?? null;

  const { topicIndices, indicesSentiment, topicSentiments, topicIntents } =
    useMemo(() => buildDrawerSequenceMeta(sequences), [sequences]);
  const { classifierIndices, decisionTags } = useMemo(
    () => buildDrawerClassifierMeta(classifierSequences),
    [classifierSequences],
  );

  const sentimentMeta = useMemo(() => {
    const enriched =
      highlightEntry?.sentiment != null
        ? {
            ...doc,
            drawer_sentiment: highlightEntry.sentiment,
            num_sentiment: highlightEntry.sentiment,
          }
        : doc;
    return resolveDrawerSentimentMeta(enriched);
  }, [doc, highlightEntry?.sentiment]);

  const topicTags = useMemo(() => {
    if (Array.isArray(associatedTopics) && associatedTopics.length) {
      return resolveDrawerTopicTagsFromHighlight(
        associatedTopics,
        topicSentiments,
        topicIntents,
      );
    }
    return resolveDrawerTopicTags(doc);
  }, [associatedTopics, topicSentiments, topicIntents, doc]);

  const [highlightedText, setHighlightedText] = useState([]);
  const [clickedTopicKey, setClickedTopicKey] = useState(null);
  const [clickedDecision, setClickedDecision] = useState(null);
  const [feedbackOpening, setFeedbackOpening] = useState(false);
  const [sentimentOverride, setSentimentOverride] = useState(null);
  const [intentOverride, setIntentOverride] = useState(null);
  const [savingClassification, setSavingClassification] = useState(false);
  const [topicOverrides, setTopicOverrides] = useState({});
  const [classifierDraft, setClassifierDraft] = useState({
    mPublish: null,
    mOpenCase: null,
    mAction: null,
  });
  const [classifierDirty, setClassifierDirty] = useState(false);
  const reviewTextRef = useRef(null);

  const pivotEntries = useMemo(
    () => welcomeDrawerReviewPivotEntries(doc?.pivot),
    [doc?.pivot],
  );
  const classifierOptions = useMemo(() => buildDrawerClassifierOptions(t), [t]);

  const overallSentimentOptions = useMemo(
    () => buildDrawerOverallSentimentOptions(t),
    [t],
  );
  const overallIntentOptions = useMemo(
    () => buildDrawerOverallIntentOptions(t),
    [t],
  );
  const topicSentimentOptions = useMemo(
    () => buildDrawerTopicSentimentOptions(t),
    [t],
  );

  const selectedSentiment = useMemo(
    () =>
      findDrawerSelectOption(
        overallSentimentOptions,
        sentimentOverride ?? sentimentMeta?.key,
      ),
    [overallSentimentOptions, sentimentMeta?.key, sentimentOverride],
  );
  const selectedIntent = useMemo(() => {
    const intentKey =
      intentOverride ??
      (highlightEntry?.intent != null
        ? highlightEntry.intent
        : doc?.drawer_intent ?? doc?.num_intent);
    return findDrawerSelectOption(overallIntentOptions, intentKey);
  }, [
    overallIntentOptions,
    highlightEntry?.intent,
    doc,
    intentOverride,
  ]);

  const gpid = doc?.date_gpid?.split?.("_")?.[1] || doc?.gpid || "";
  const internalId = doc?.internalId || doc?.internal_id || "";
  const showFeedbackLink =
    isFeedbackEnabled !== false && canOpenDrawerFeedback(doc);

  const handleOpenFeedback = useCallback(async () => {
    if (feedbackOpening) return;
    setFeedbackOpening(true);
    try {
      const url = await resolveDrawerFeedbackUrlAsync(doc);
      if (!url) {
        notification.warning({
          message: isEmbedFeedbackReview(doc)
            ? t("team_kpi_view.70")
            : t("team_kpi_view.71"),
        });
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setFeedbackOpening(false);
    }
  }, [doc, feedbackOpening, t]);

  const correctionBasePayload = useCallback(() => {
    const dashboardId = doc?.dash_id ?? doc?.dashboard_id;
    const dateGpid = doc?.date_gpid;
    if (!dashboardId || !dateGpid) return null;
    return {
      dashboard_id: dashboardId,
      Gpid: String(gpid || dateGpid),
      date_gpid: dateGpid,
      language: i18n.language || "en",
      review_text: text,
      ...(internalId ? { internal_id: internalId } : {}),
    };
  }, [doc, gpid, i18n.language, internalId, text]);

  const saveClassification = useCallback(
    async (type, wrongValue, correctValue, onOk) => {
      const base = correctionBasePayload();
      if (!base || savingClassification) return;
      if (String(wrongValue ?? "") === String(correctValue ?? "")) return;
      setSavingClassification(true);
      onOk(correctValue);
      try {
        const result = await apiRequest(
          "dashboards/correction",
          "POST",
          {
            ...base,
            type,
            wrong_value: wrongValue == null ? "" : String(wrongValue),
            correct_value: String(correctValue),
          },
          true,
        );
        if (result?.success) {
          notification.success({
            message: result.success,
          });
          if (typeof onHighlightsRefresh === "function") {
            onHighlightsRefresh();
          }
        } else {
          notification.error({
            message: result?.error || t("review.5", "Could not save feedback"),
          });
        }
      } catch (err) {
        notification.error({
          message: t("review.5", "Could not save feedback"),
        });
      } finally {
        setSavingClassification(false);
      }
    },
    [correctionBasePayload, onHighlightsRefresh, savingClassification, t],
  );

  const tagEditEnabled =
    Boolean(feedbackModeEnabled) && Boolean(doc?.dash_id || doc?.dashboard_id);

  const handleTopicTagChange = useCallback((topic, field, option) => {
    if (!option?.value || savingClassification) return;
    setTopicOverrides((prev) =>
      nextDrawerTopicOverride(prev, topic, field, option),
    );
  }, [savingClassification]);

  const handleDiscardTopicEdits = useCallback(() => {
    setTopicOverrides({});
  }, []);

  const handleClassifierChange = useCallback((field, value) => {
    setClassifierDraft((prev) => ({
      ...prev,
      [field]: parseDrawerClassifierSelect(value),
    }));
    setClassifierDirty(true);
  }, []);

  const handleDiscardClassifierEdits = useCallback(() => {
    setClassifierDraft({ mPublish: null, mOpenCase: null, mAction: null });
    setClassifierDirty(false);
  }, []);

  const clearTopicSelection = useCallback(() => {
    setClickedTopicKey(null);
    setHighlightedText([]);
    setClickedDecision(null);
    setTopicOverrides({});
    setClassifierDraft({ mPublish: null, mOpenCase: null, mAction: null });
    setClassifierDirty(false);
  }, []);

  const handleApplyClassifierEdits = useCallback(async () => {
    if (savingClassification || !classifierDirty) return;
    setSavingClassification(true);
    try {
      const result = await applyDrawerClassifierFeedback({
        doc,
        sequences,
        classifierSequences,
        relatedTopics: associatedTopics,
        highlightedSpans: highlightedText,
        clickedDecision,
        classifierValues: classifierDraft,
        language: i18n.language,
      });
      if (result?.success) {
        notification.success({
          message: t("review.feedback_applied", "Feedback applied successfully."),
        });
        handleDiscardClassifierEdits();
        clearTopicSelection();
        if (typeof onHighlightsRefresh === "function") {
          onHighlightsRefresh();
        }
      } else {
        notification.error({
          message: result?.error || t("review.5", "Could not save feedback"),
        });
      }
    } catch (err) {
      notification.error({
        message: t("review.5", "Could not save feedback"),
      });
    } finally {
      setSavingClassification(false);
    }
  }, [
    associatedTopics,
    classifierDirty,
    classifierDraft,
    classifierSequences,
    clickedDecision,
    clearTopicSelection,
    doc,
    handleDiscardClassifierEdits,
    highlightedText,
    i18n.language,
    onHighlightsRefresh,
    savingClassification,
    sequences,
    t,
  ]);

  const handleApplyTopicEdits = useCallback(async () => {
    if (savingClassification) return;
    const isolated = (Array.isArray(topicTags) ? topicTags : []).find(
      (topic) => drawerTopicKey(topic) === clickedTopicKey,
    );
    const override = isolated ? topicOverrides[isolated.id] : null;
    if (!isolated || !override) return;
    setSavingClassification(true);
    try {
      const result = await applyDrawerTopicFeedback({
        doc,
        sequences,
        classifierSequences,
        relatedTopics: associatedTopics,
        highlightedSpans: highlightedText,
        topic: isolated,
        sentiment: override.sentiment ?? isolated.sentiment,
        intent: override.intent ?? isolated.intent,
        language: i18n.language,
      });
      if (result?.success) {
        notification.success({
          message: t("review.feedback_applied", "Feedback applied successfully."),
        });
        setTopicOverrides({});
        clearTopicSelection();
        if (typeof onHighlightsRefresh === "function") {
          onHighlightsRefresh();
        }
      } else {
        notification.error({
          message: result?.error || t("review.5", "Could not save feedback"),
        });
      }
    } catch (err) {
      notification.error({
        message: t("review.5", "Could not save feedback"),
      });
    } finally {
      setSavingClassification(false);
    }
  }, [
    associatedTopics,
    classifierSequences,
    clearTopicSelection,
    clickedTopicKey,
    doc,
    highlightedText,
    i18n.language,
    onHighlightsRefresh,
    savingClassification,
    sequences,
    t,
    topicOverrides,
    topicTags,
  ]);

  const handleTopicClick = useCallback(
    (topic) => {
      const topicKey = nextClickedTopicKey(clickedTopicKey, topic);
      if (!topicKey) {
        setClickedTopicKey(null);
        setHighlightedText([]);
        setClickedDecision(null);
        setTopicOverrides({});
        return;
      }

      const indices =
        topicIndices?.[topic.id] || topicIndices?.[String(topic.id)] || [];
      const nextHighlight = Array.isArray(indices) && indices.length
        ? [...indices]
            .sort((a, b) => a[0] - b[0])
            .map(([start, end]) => ({
              text: text.substring(start, end + 1),
              indices: [start, end],
              sentiment: indicesSentiment[`${start}, ${end}`] ?? "0.5",
            }))
        : [];

      setClickedTopicKey(topicKey);
      setClickedDecision(null);
      setTopicOverrides({});
      handleDiscardClassifierEdits();
      setHighlightedText(nextHighlight);
    },
    [clickedTopicKey, handleDiscardClassifierEdits, indicesSentiment, text, topicIndices],
  );

  const topicIsolated = isDrawerTopicIsolated(tagEditEnabled, clickedTopicKey);
  const visibleTopicTags = visibleDrawerTopicTags(
    topicTags,
    tagEditEnabled,
    clickedTopicKey,
  );

  const handleDecisionTagClick = useCallback(
    (label) => {
      if (clickedDecision === label && highlightedText.length) {
        setClickedDecision(null);
        setHighlightedText([]);
        setClickedTopicKey(null);
        handleDiscardClassifierEdits();
        return;
      }

      const indices = classifierIndices?.[label] || [];
      if (!indices.length) return;

      indices.sort((a, b) => a[0] - b[0]);
      const nextHighlight = indices.map(([start, end]) => ({
        text: text.substring(start, end + 1),
        indices: [start, end],
      }));

      setClickedDecision(label);
      setClickedTopicKey(null);
      setTopicOverrides({});
      handleDiscardClassifierEdits();
      setHighlightedText(nextHighlight);
    },
    [clickedDecision, classifierIndices, handleDiscardClassifierEdits, highlightedText.length, text],
  );

  const showClassifierControls =
    tagEditEnabled &&
    highlightedText.length > 0 &&
    !clickedTopicKey;

  const handleCardMouseDown = useCallback(
    (event) => {
      const hasActiveSelection =
        Boolean(clickedTopicKey) ||
        highlightedText.length > 0 ||
        Boolean(clickedDecision);
      if (
        !hasActiveSelection ||
        hasPendingDrawerTopicEdits(topicOverrides) ||
        classifierDirty
      ) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;

      const keepSelection = target.closest(
        [
          "button",
          "a",
          "input",
          "textarea",
          "select",
          '[role="button"]',
          ".ant-select",
          ".ant-select-dropdown",
          ".ant-popover",
          '[data-testid="drawer-topic-apply-bar"]',
          '[data-testid="drawer-classifier-apply-bar"]',
          '[data-testid="drawer-classifier-controls"]',
          '[data-testid="drawer-isolated-topic"]',
          '[data-testid^="drawer-topic-tag-"]',
          '[data-testid="topic-dropdown"]',
        ].join(", "),
      );
      if (keepSelection) return;

      if (reviewTextRef.current?.contains(target)) return;

      clearTopicSelection();
    },
    [
      clickedDecision,
      clickedTopicKey,
      classifierDirty,
      clearTopicSelection,
      highlightedText.length,
      topicOverrides,
    ],
  );

  useEffect(() => {
    setHighlightedText([]);
    setClickedTopicKey(null);
    setClickedDecision(null);
    setSentimentOverride(null);
    setIntentOverride(null);
    setTopicOverrides({});
    setClassifierDraft({ mPublish: null, mOpenCase: null, mAction: null });
    setClassifierDirty(false);
  }, [doc?.date_gpid]);

  if (!text && !title) {
    return null;
  }

  return (
    <article
      className={styles.card}
      data-review-id={doc?.date_gpid || doc?.gpid || ""}
      onMouseDown={handleCardMouseDown}
    >
      {title ? <h4 className={styles.title}>{title}</h4> : null}
      <div className={styles.meta}>
        {Number.isFinite(rating) && rating > 0 ? (
          <StarRatings
            rating={rating}
            starRatedColor="#f5a623"
            numberOfStars={5}
            starDimension="16px"
            starSpacing="2px"
            name={`welcome-drawer-rating-${doc?.date_gpid || "x"}`}
          />
        ) : null}
        {dateLabel ? (
          <span className={styles.date} data-testid="drawer-review-date">
            {dateLabel}
          </span>
        ) : null}
        {showFeedbackLink ? (
          <button
            type="button"
            className={styles.feedbackLink}
            title={t("team_kpi_view.69")}
            disabled={feedbackOpening}
            onClick={handleOpenFeedback}
          >
            <EditOutlined aria-hidden />
            <span>{t("team_kpi_view.68")}</span>
          </button>
        ) : null}
      </div>
      {text ? (
        <p
          ref={reviewTextRef}
          className={styles.text}
          data-testid="drawer-review-text"
        >
          <WelcomeDrawerReviewTextHighlight
            search={highlightedText}
            child={text}
            theme={theme}
          />
        </p>
      ) : null}

      {pivotEntries.length > 0 ? (
        <div className={styles.tagSection}>
          {pivotEntries.map((entry) => (
            <span key={entry.key} className={styles.pivotValue}>
              {entry.label}
            </span>
          ))}
        </div>
      ) : null}

      {decisionTags.length > 0 ? (
        <div className={styles.tagSection}>
          {decisionTags.map((tag) => {
            const isActive = clickedDecision === tag;
            const label = translateDecisionLabel(tag, t);
            return (
              <ReviewActionTag
                key={tag}
                as="button"
                label={label}
                active={isActive}
                isDark={theme === "dark"}
                className={styles.compactActionTag}
                onClick={() => handleDecisionTagClick(tag)}
              />
            );
          })}
        </div>
      ) : null}

      {showClassifierControls ? (
        <div
          className={styles.tagSection}
          data-testid="drawer-classifier-controls"
        >
          {shouldShowDrawerClassifierField(clickedDecision, "mPublish") ? (
            <Select
              size="small"
              className={styles.tagSelect}
              popupClassName={styles.tagSelectDropdown}
              getPopupContainer={() => document.body}
              placeholder={classifierOptions.placeholders.publish}
              value={drawerClassifierSelectValue(classifierDraft.mPublish)}
              disabled={savingClassification}
              options={classifierOptions.publish}
              onChange={(value) => handleClassifierChange("mPublish", value)}
            />
          ) : null}
          {shouldShowDrawerClassifierField(clickedDecision, "mOpenCase") ? (
            <Select
              size="small"
              className={styles.tagSelect}
              popupClassName={styles.tagSelectDropdown}
              getPopupContainer={() => document.body}
              placeholder={classifierOptions.placeholders.openCase}
              value={drawerClassifierSelectValue(classifierDraft.mOpenCase)}
              disabled={savingClassification}
              options={classifierOptions.openCase}
              onChange={(value) => handleClassifierChange("mOpenCase", value)}
            />
          ) : null}
          {shouldShowDrawerClassifierField(clickedDecision, "mAction") ? (
            <Select
              size="small"
              className={styles.tagSelect}
              popupClassName={styles.tagSelectDropdown}
              getPopupContainer={() => document.body}
              placeholder={classifierOptions.placeholders.action}
              value={drawerClassifierSelectValue(classifierDraft.mAction)}
              disabled={savingClassification}
              options={classifierOptions.action}
              onChange={(value) => handleClassifierChange("mAction", value)}
            />
          ) : null}
        </div>
      ) : null}

      {(sentimentMeta || selectedIntent || visibleTopicTags.length > 0) && (
        <div className={styles.tagSection}>
          {!topicIsolated && sentimentMeta?.barValue != null ? (
            <RangeBar value={sentimentMeta.barValue} />
          ) : null}
          {!topicIsolated && tagEditEnabled ? (
            <span data-testid="drawer-overall-classification">
            <Select
              size="small"
              className={styles.tagSelect}
              popupClassName={styles.tagSelectDropdown}
              getPopupContainer={() => document.body}
              value={selectedSentiment?.value}
              disabled={savingClassification}
              options={overallSentimentOptions.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              onChange={(value) =>
                saveClassification(
                  "sentiment",
                  sentimentMeta?.key,
                  value,
                  setSentimentOverride,
                )
              }
            />
            <Select
              size="small"
              className={styles.tagSelect}
              popupClassName={styles.tagSelectDropdown}
              getPopupContainer={() => document.body}
              value={selectedIntent?.value}
              disabled={savingClassification}
              options={overallIntentOptions.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              onChange={(value) => {
                const current =
                  highlightEntry?.intent != null
                    ? highlightEntry.intent
                    : doc?.drawer_intent ?? doc?.num_intent;
                saveClassification("intent", current, value, setIntentOverride);
              }}
            />
            </span>
          ) : !topicIsolated ? (
            <>
            <DrawerPill
              icon={selectedSentiment?.img}
              label={selectedSentiment?.label}
            />
            <DrawerPill
              icon={selectedIntent?.img}
              label={selectedIntent?.label}
            />
            </>
          ) : null}
          {visibleTopicTags.map((topic) => {
            const override = topicOverrides[topic.id];
            const sentimentKey = override?.sentiment ?? topic.sentiment;
            const intentKey = override?.intent ?? topic.intent;
            const colors = resolveTopicTagColors(topic.type, theme);
            const topicSentiment = findDrawerSelectOption(
              topicSentimentOptions,
              sentimentKey,
            );
            const selectedIntentOpt = findDrawerSelectOption(
              overallIntentOptions,
              intentKey,
            );
            const sentimentIcon =
              topicSentiment?.img ||
              (sentimentKey != null && sentimentKey !== ""
                ? resolveTopicTagSentimentIcon(sentimentKey)
                : null);
            const intentIcon = intentKey
              ? resolveTopicTagIntentIcon(intentKey)
              : null;
            const topicKey = drawerTopicKey(topic);
            const isActive = clickedTopicKey === topicKey;
            if (shouldRenderIsolatedTopicDropdowns(tagEditEnabled, topicIsolated)) {
              return (
                <div
                  key={topicKey}
                  data-testid="drawer-isolated-topic"
                  className={`${styles.topicTag} ${styles.topicTagEditable} ${
                    isActive ? styles.topicTagActive : ""
                  }`}
                  style={{
                    backgroundColor: colors.background,
                    color: colors.color,
                  }}
                  onClick={() => handleTopicClick(topic)}
                >
                  <span
                    className={styles.topicSentimentTrigger}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Dropdown
                      bgColor={colors.background}
                      options={topicSentimentOptions}
                      disableMenu={savingClassification}
                      dropdownClassName={styles.topicDropdownOverlay}
                      getPopupContainer={() => document.body}
                      selectedOptions={topicSentiment}
                      setSelectedOptions={(opt) =>
                        handleTopicTagChange(topic, "sentiment", opt)
                      }
                    />
                  </span>
                  <span
                    className={styles.topicSentimentTrigger}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Dropdown
                      bgColor={colors.background}
                      options={overallIntentOptions}
                      disableMenu={savingClassification}
                      dropdownClassName={styles.topicDropdownOverlay}
                      getPopupContainer={() => document.body}
                      selectedOptions={selectedIntentOpt}
                      setSelectedOptions={(opt) =>
                        handleTopicTagChange(topic, "intent", opt)
                      }
                    />
                  </span>
                  <span
                    className={styles.topicLabelButton}
                    title={topic.label}
                  >
                    <span className={styles.topicLabel}>{topic.label}</span>
                  </span>
                </div>
              );
            }
            return (
              <button
                key={topicKey}
                type="button"
                data-testid={`drawer-topic-tag-${topic.id}`}
                className={`${styles.topicTag} ${styles.topicTagButton} ${
                  isActive ? styles.topicTagActive : ""
                }`}
                style={{
                  backgroundColor: colors.background,
                  color: colors.color,
                }}
                title={topic.label}
                aria-pressed={isActive}
                onClick={() => handleTopicClick(topic)}
              >
                {sentimentIcon ? <img src={sentimentIcon} alt="" /> : null}
                {intentIcon ? <img src={intentIcon} alt="" /> : null}
                <span className={styles.topicLabel}>{topic.label}</span>
              </button>
            );
          })}
          {topicIsolated && hasPendingDrawerTopicEdits(topicOverrides) ? (
            <div
              className={styles.topicEditActions}
              data-testid="drawer-topic-apply-bar"
            >
              <Button
                size="small"
                data-testid="drawer-topic-discard"
                disabled={savingClassification}
                onClick={handleDiscardTopicEdits}
              >
                {t("top_topics.21", "Discard")}
              </Button>
              <Button
                size="small"
                type="primary"
                data-testid="drawer-topic-apply"
                disabled={savingClassification}
                onClick={handleApplyTopicEdits}
              >
                {t("review.apply_changes", "Apply Changes")}
              </Button>
            </div>
          ) : null}
        </div>
      )}

      {showClassifierControls && classifierDirty ? (
        <div
          className={styles.topicEditActions}
          data-testid="drawer-classifier-apply-bar"
        >
          <Button
            size="small"
            data-testid="drawer-classifier-discard"
            disabled={savingClassification}
            onClick={handleDiscardClassifierEdits}
          >
            {t("top_topics.21", "Discard")}
          </Button>
          <Button
            size="small"
            type="primary"
            data-testid="drawer-classifier-apply"
            disabled={savingClassification}
            onClick={handleApplyClassifierEdits}
          >
            {t("review.apply_changes", "Apply Changes")}
          </Button>
        </div>
      ) : null}

      {gpid ? (
        <details className={styles.footer}>
          <summary>{t("common.details", "Details")}</summary>
          <div className={styles.metaLines}>
            <span>Gpid: {gpid}</span>
            {internalId ? <span>UniqueReviewId: {internalId}</span> : null}
          </div>
        </details>
      ) : null}
    </article>
  );
}
